import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaletteIcon from '@mui/icons-material/Palette';
import { useAppTheme } from '../../contexts/ThemeContext';

const ThemeCustomization = () => {
  const { currentThemeKey, currentTheme, themeOptions, setTheme } = useAppTheme();
  const [previewTheme, setPreviewTheme] = useState(null);

  const handleThemeSelect = (themeKey) => {
    setTheme(themeKey);
    setPreviewTheme(null);
  };

  const ThemePreviewCard = ({ themeKey, themeData, isSelected, isPreview }) => (
    <Card 
      sx={{ 
        position: 'relative',
        cursor: 'pointer',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease',
        transform: isPreview ? 'scale(1.02)' : 'scale(1)',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 3
        }
      }}
      onClick={() => handleThemeSelect(themeKey)}
    >
      {isSelected && (
        <Box sx={{ 
          position: 'absolute', 
          top: 8, 
          right: 8, 
          zIndex: 1 
        }}>
          <CheckCircleIcon color="primary" />
        </Box>
      )}
      
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="h6" gutterBottom>
          {themeData.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {themeData.description}
        </Typography>
        
        {/* Color Preview */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Box sx={{
            width: 40,
            height: 20,
            backgroundColor: themeData.primary,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }} />
          <Box sx={{
            width: 40,
            height: 20,
            backgroundColor: themeData.secondary,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }} />
        </Box>
        
        {/* Sample UI Elements Preview */}
        <Box sx={{ 
          bgcolor: 'grey.50', 
          p: 1.5, 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            size="small" 
            variant="contained"
            sx={{ 
              backgroundColor: themeData.primary,
              '&:hover': { backgroundColor: themeData.primary },
              mb: 1,
              mr: 1
            }}
          >
            Primary
          </Button>
          <Button 
            size="small" 
            variant="outlined"
            sx={{ 
              borderColor: themeData.primary,
              color: themeData.primary,
              '&:hover': { 
                borderColor: themeData.primary,
                backgroundColor: `${themeData.primary}10`
              }
            }}
          >
            Outlined
          </Button>
          <br />
          <Chip 
            label="Sample Chip" 
            size="small"
            sx={{ 
              backgroundColor: themeData.secondary,
              color: 'white',
              mt: 1
            }}
          />
        </Box>
      </CardContent>
      
      <CardActions sx={{ pt: 0 }}>
        <Button 
          size="small" 
          variant={isSelected ? "contained" : "outlined"}
          fullWidth
          sx={{
            backgroundColor: isSelected ? themeData.primary : 'transparent',
            borderColor: themeData.primary,
            color: isSelected ? 'white' : themeData.primary,
            '&:hover': {
              backgroundColor: isSelected ? themeData.primary : `${themeData.primary}10`,
            }
          }}
        >
          {isSelected ? 'Current Theme' : 'Select Theme'}
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PaletteIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Theme Customization
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Choose a color theme that best fits your clinic's branding. The theme will be applied throughout the entire application.
        </Typography>
      </Alert>
      
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
        Current Theme: <strong>{currentTheme.name}</strong>
      </Typography>
      
      <Grid container spacing={3}>
        {Object.entries(themeOptions).map(([themeKey, themeData]) => (
          <Grid item xs={12} sm={6} md={4} key={themeKey}>
            <ThemePreviewCard
              themeKey={themeKey}
              themeData={themeData}
              isSelected={currentThemeKey === themeKey}
              isPreview={previewTheme === themeKey}
            />
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> Theme changes are saved automatically and will persist across sessions. 
          The selected theme affects all UI elements including buttons, links, navigation, and form components.
        </Typography>
      </Box>
    </Paper>
  );
};

export default ThemeCustomization; 